import React, { ReactNode } from "react";
import moment, { Moment } from "moment";
import { isNumber } from "highcharts";
import debug from "./debug";
import monday from "./tools/monday";
import { LinearProgressBar } from "monday-ui-react-core";
import { Board, Labels, Column } from "./types";
import CumulativeFlowDiagramView from "./CumulativeFlowDiagramView";
import ReplayView from "./replayView";
import StatsView from "./statsView";
import TreeView from "./treeView";
import { StatType } from "./tools/stats";
import {
    SettingsProvider,
    Settings,
    Context,
    DEFAULT_CONTEXT,
    DEFAULT_SETTINGS,
    AppView,
    Sorting
} from "./SettingsProvider";
import Loading from "./common/Loading";
import Toast from "./common/Toast";
import SettingErrors from "./SettingErrors";
import DemoOverlay from "./DemoOverlay";
import { generateColumns, generateItems, generateActivityLogs, GeneratorParams } from "./fixtures/generator";
import { BoardData, BoardDataResult, DataError, getBoardData, sanitiseColorColumn } from "./tools/data";
import { sleep, isRetrospectiveTools } from "./tools";
import FunnelView from "./funnelView";
import TopMenuFunnels from "./common/TopMenuFunnels";

import "./App.css";

const log = debug("App");

type Props = {
    view: AppView;
    demo?: string;
};

type State = {
    settings: Settings;
    context: Context;
    boards?: Board[];
    itemIds?: number[];
    labels: Labels;
    allFetchedColumns: Set<string>;
    needsRefresh: number;
    errors?: string[];
    criticalErrors?: any[];
    rollbarUUID?: string;
    loading: boolean;
    isLoadingPercentage: number;
    demoParams: GeneratorParams | null;
};

export default class App extends React.Component<Props, State> {
    constructor(props: Props) {
        super(props);
        const demoParams: GeneratorParams =
            props.demo !== undefined ? require("./fixtures/" + props.demo).default : null;

        this.state = {
            settings: demoParams ? demoParams.mondaySettings : DEFAULT_SETTINGS,
            context: demoParams ? demoParams.mondayContext : DEFAULT_CONTEXT,
            itemIds: undefined,
            boards: [],
            labels: { names: {}, order: {}, orderedIds: [], labelsColors: {} },
            allFetchedColumns: new Set(),
            needsRefresh: 0,
            loading: true,
            isLoadingPercentage: 0,
            demoParams
        };
    }

    //
    // Roolbar docs:
    // - https://docs.rollbar.com/docs/javascript
    // - https://docs.rollbar.com/docs/rollbarjs-configuration-reference
    //
    rollbarError = (msg: string, extra?: any, err?: Error): void => {
        window.Rollbar?.error(msg, extra, err, (e: Error, data: any): void => {
            if (e) {
                console.error("Error reporting error to Rollbar: ", err);
            } else {
                console.log("Error reported to Rollbar. UUID: ", data.result.uuid);
                this.setState({ rollbarUUID: data.result.uuid });
            }
        });
    };

    componentDidMount(): void {
        // https://monday.com/developers/apps/advanced-view/
        monday.listen("settings", (res: any) => {
            log(`event: settings: ${JSON.stringify(res.data)}`);

            const newSettings: Partial<Settings> = {};

            const { data } = res;

            const getSelectedColumn = (columnData: { [id: string]: boolean }): string | undefined => {
                const selectedColumns: string[] = Object.keys(columnData).filter((key) => columnData[key]);
                return selectedColumns?.length > 0 ? selectedColumns[0] : undefined;
            };

            if (data.columns) {
                newSettings.column = getSelectedColumn(data.columns);
            }

            if (data.numberColumn) {
                newSettings.numberColumn = getSelectedColumn(data.numberColumn);
            }

            if (data.fromDate) {
                // beginning of the day
                const from = moment(data.fromDate).startOf("day");
                newSettings.from = from;
            }

            if (data.toDate) {
                // until this hour if end date is today
                let to = moment(data.toDate).startOf("day");
                if (to.isAfter(moment())) {
                    to = moment().startOf("hour");
                }
                newSettings.to = to;
            }

            if (data.itemSorting) {
                newSettings.itemSorting = (Sorting as any)[data.itemSorting];
            }

            if (data.showStats) {
                newSettings.showStats = data.showStats
                    .map((stat: string) => (StatType as any)[stat])
                    .filter((stat: StatType) => stat !== undefined);
            }

            newSettings.statsShowUnselectedBars = data.statsShowUnselectedBars;
            newSettings.statsCompareAgainst = (StatType as any)[data.statsCompareAgainst];

            newSettings.showPrevNextEvents = data.showPrevNextEvents;
            newSettings.showProgress = data.showProgress;

            newSettings.autoupdate = data.autoupdate;

            newSettings.scaleBasedOnCount = data.scaleBasedOnCount;
            newSettings.mergeStatuses = data.mergeStatuses;
            newSettings.firstStatusAsWorkflowStart = data.firstStatusAsWorkflowStart;
            newSettings.lastStatusAsWorkflowEnd = data.lastStatusAsWorkflowEnd;
            newSettings.showDeadEnds = data.showDeadEnds;
            newSettings.filterNodeWeightLessThan = data.filterNodeWeightLessThan
                ? parseFloat(data.filterNodeWeightLessThan)
                : undefined;
            newSettings.filterLevelsAbove = data.filterLevelsAbove ? parseInt(data.filterLevelsAbove) : undefined;
            newSettings.nodeHeightRescale = data.nodeHeightRescale;
            const unit: { symbol: "custom" | string; custom_unit: string; direction?: "left" | "right" } | undefined =
                data.unit;

            if (unit) {
                const unitStr = unit.symbol === "custom" ? unit.custom_unit : unit.symbol;
                if (unit.direction === "left") {
                    newSettings.unitTag = (_, value): string => `${unitStr}${value}`;
                } else {
                    newSettings.unitTag = (_, value): string => `${value}${unitStr}`;
                }
            }

            newSettings.view = data.view || this.props.view;

            if (newSettings.view && [AppView.TREE, AppView.FUNNEL].includes(newSettings.view)) {
                newSettings.from = moment(0).startOf("day");
            }

            const errors = this.checkSettings(newSettings);

            if (errors.length > 0) {
                this.setState({ errors });
                return;
            }

            const needsDataRefresh =
                newSettings.column !== this.state.settings.column ||
                newSettings.numberColumn !== this.state.settings.numberColumn ||
                (newSettings.from && !newSettings.from.isSame(this.state.settings.from)) ||
                (newSettings.to && !newSettings.to.isSame(this.state.settings.to)) ||
                newSettings.view !== this.state.settings.view;

            this.setState(
                (prevState) => ({
                    settings: {
                        ...prevState.settings,
                        ...newSettings
                    },
                    errors: undefined,
                    loading: needsDataRefresh
                }),
                () => {
                    if (needsDataRefresh) {
                        this.fetchData();
                    }
                }
            );
        });

        monday.listen("context", (res: any) => {
            log(`event: context: ${JSON.stringify(res.data)}`);

            const newContext = res.data as Context;
            const needsDataRefresh = newContext.boardIds[0] !== this.state.context.boardIds[0];

            this.setState(
                (prevState) => {
                    const body = document.body;
                    const prevTheme = prevState.context?.theme;
                    if (prevTheme) {
                        body.classList.remove(`App--${prevTheme}`);
                        body.classList.remove(`${prevTheme}-app-theme`);
                    }
                    body.classList.add(`App--${newContext.theme}`);
                    body.classList.add(`${newContext.theme}-app-theme`);
                    return { context: newContext };
                },
                () => {
                    if (needsDataRefresh) {
                        this.fetchData();
                    }
                }
            );

            if (window.Rollbar) {
                window.Rollbar.configure({
                    payload: {
                        // these are visible in Rollbar RQL view e.g. "SELECT * FROM item_occurrence WHERE boardIds = 653447291"
                        instanceId: newContext.instanceId,
                        boardIds: newContext.boardIds.join(", ")
                    }
                });
            }
        });

        monday.listen("events", (res: any) => {
            const { settings } = this.state;
            const { data } = res;

            log(`event: ${JSON.stringify(res)}`);

            if (
                (res.type === "change_column_values" && this.state.allFetchedColumns.has(data.columnId)) ||
                res.type === "new_items"
            ) {
                if (settings.autoupdate) {
                    this.fetchData();
                } else {
                    this.setState((prevState) => ({ needsRefresh: prevState.needsRefresh + 1 }));
                }
            }
        });

        monday.listen("itemIds", (res: any) => {
            log(`event: itemIds`);

            // check new itemIds differ
            const newItemIds: number[] = res.data || [];
            const itemIdSet = new Set(this.state.itemIds);
            if (newItemIds.length === this.state.itemIds?.length && !newItemIds.some((id) => !itemIdSet.has(id))) {
                return;
            }

            this.setState({ itemIds: res.data });
            this.fetchData();
        });

        if (this.state.demoParams) {
            this.setState({ itemIds: [0] }, this.fetchData);
        }
    }

    checkSettings = (settings: Settings): string[] => {
        const errors: string[] = [];

        const checkDates = (from: Moment | undefined, to: Moment | undefined): void => {
            if (!moment.isMoment(from)) {
                errors.push('"From Date" must be specified');
            }
            if (!moment.isMoment(to)) {
                errors.push('"To Date" must be specified');
            }
            if (from && to && from.isSameOrAfter(to)) {
                errors.push('"From Date" must be before "To Date"');
            }
        };

        const checkColumnId = (columnId: string | undefined): void => {
            if (!columnId) errors.push("Status column must be specified");
        };

        const checkNumericalValues = (filterLevelsAbove?: number, filterNodeWeightLessThan?: number): void => {
            if (filterLevelsAbove !== undefined && !isNumber(filterLevelsAbove)) {
                errors.push('"Hide levels above" has to be empty or an integer number.');
            }
            if (filterNodeWeightLessThan !== undefined && !isNumber(filterNodeWeightLessThan)) {
                errors.push('"Hide nodes with weight less than" has to be empty or a number.');
            }
        };

        if (settings.view && ![AppView.TREE, AppView.FUNNEL].includes(settings.view)) {
            checkDates(settings.from, settings.to);
        }
        checkColumnId(settings.column);
        if (settings.view === AppView.TREE) {
            checkNumericalValues(settings.filterLevelsAbove, settings.filterNodeWeightLessThan);
        }

        return errors;
    };

    fetchData = async (): Promise<void> => {
        const { context, settings, itemIds, demoParams } = this.state;
        const { demo } = this.props;

        if (!context.boardIds || !context.boardIds.length || !settings.column || !itemIds) {
            return;
        }
        log("fetching data");
        this.setState({
            loading: true,
            isLoadingPercentage: 0,
            criticalErrors: undefined,
            errors: undefined,
            needsRefresh: 0
        });

        const newState: Partial<State> = {
            loading: false
        };

        // activity_logs needs itemIds, otherwise we get all the logs, including archived or deleted items
        // items() will return only active items, i.e. archived/deleted won't be fetched
        let data: BoardDataResult | undefined = undefined;
        if (demoParams) {
            log("DEMO: Using demo data!");
            try {
                const columns = generateColumns(demoParams);
                const columnsById = columns.reduce((acc, col) => {
                    acc[col.id] = col;
                    return acc;
                }, {} as { [id: string]: Column });
                const { activityLogs, lastStatusPerItem } = generateActivityLogs(demoParams);
                await sleep(500); // hack: give time for Tree/FunnelCanvas to render components
                data = {
                    boards: [
                        {
                            id: 1,
                            columns,
                            columnsById,
                            items: generateItems(demoParams, lastStatusPerItem),
                            activity_logs: activityLogs
                        }
                    ],
                    allFetchedColumns: new Set()
                };
            } catch (err) {
                newState.criticalErrors = [err];
                console.error(`Error when getting demo data for fixture "${demo}"`, err);
                this.rollbarError("Demo data error", { demoConfig: demo }, err as Error);
            }
        } else {
            const columnIds = [settings.column];
            if (settings.numberColumn) {
                columnIds.push(settings.numberColumn);
            }
            const withActivityLogs = settings.view !== undefined && ![AppView.FUNNEL].includes(settings.view);
            data = await getBoardData(
                context.boardIds[0],
                columnIds,
                itemIds,
                settings.from!,
                withActivityLogs,
                (percentage) => {
                    this.setState({ isLoadingPercentage: percentage });
                }
            );
            if (data && (data as DataError).error_err) {
                const { error_msg, error_extra, error_err } = data as DataError;
                newState.criticalErrors = [error_err];
                this.rollbarError(error_msg, error_extra, error_err);
            }
        }

        if (data !== undefined && (data as BoardData).boards) {
            const { boards, allFetchedColumns } = data as BoardData;
            newState.boards = boards;
            newState.allFetchedColumns = allFetchedColumns;

            const board = boards[0];

            if (board && board.columns) {
                const columns: Column[] = board.columns;
                const columnsById: { [id: string]: Column } = {};
                board.columnsById = columnsById;
                columns.forEach((column) => (columnsById[column.id] = column));

                // process and sanitise "status" column settings to get label info
                const colorColumn: Column | undefined = columns.find((col) => col.type === "status");
                if (colorColumn !== undefined && colorColumn.settings_str) {
                    newState.labels = sanitiseColorColumn(colorColumn, board.activity_logs, board.items);
                }
            }
        }

        this.setState(newState as State);
    };

    handleRefresh = (): void => {
        this.setState({ needsRefresh: 0 });
        this.fetchData();
    };

    static getDerivedStateFromError(error: Error): Partial<State> {
        return { criticalErrors: [error] };
    }

    componentDidCatch(error: any, errorInfo: any): void {
        this.rollbarError("Uncaught error", { errorInfo }, error);
    }

    render(): ReactNode {
        const {
            errors,
            criticalErrors,
            rollbarUUID,
            settings,
            context,
            boards,
            labels,
            needsRefresh,
            loading,
            demoParams
        } = this.state;
        const { demo } = this.props;
        const board = boards && boards.length ? boards[0] : undefined;

        let viewComponent = null;
        if (criticalErrors) {
            viewComponent = (
                <Toast
                    type="alert"
                    text={`Oops, something went wrong.${
                        rollbarUUID
                            ? " Reload the page or contact support@omnitas.dev with this error UUID: " +
                              rollbarUUID +
                              "."
                            : ".."
                    }`}
                    buttonText="Retry"
                    onButtonClick={this.handleRefresh}
                />
            );
        } else if (errors) {
            viewComponent = <SettingErrors errors={errors} />;
        } else {
            if (settings.column && boards && board && labels && (!loading || settings.view === AppView.CFD)) {
                switch (settings.view) {
                    case AppView.CFD:
                        viewComponent = (
                            <CumulativeFlowDiagramView
                                board={board}
                                labels={labels}
                                columnId={settings.column}
                                from={settings.from!}
                                to={settings.to!}
                                loading={loading}
                            />
                        );
                        break;
                    case AppView.REPLAY:
                        viewComponent = (
                            <ReplayView
                                board={board}
                                labels={labels}
                                columnId={settings.column}
                                from={settings.from!}
                                to={settings.to!}
                                showPrevNextEvents={settings.showPrevNextEvents || false}
                                showProgress={settings.showProgress || false}
                            />
                        );
                        break;
                    case AppView.STATS:
                        viewComponent = (
                            <StatsView
                                board={board}
                                labels={labels}
                                columnId={settings.column}
                                from={settings.from!}
                                to={settings.to!}
                                showStats={settings.showStats!}
                                globalNormalise
                                itemSorting={settings.itemSorting!}
                            />
                        );
                        break;
                    case AppView.TREE:
                        viewComponent = (
                            <TreeView
                                board={board}
                                initialTrackedLabels={
                                    this.state.demoParams ? this.state.demoParams.initialSelectedStatuses : undefined
                                }
                            />
                        );
                        break;
                    case AppView.FUNNEL:
                        viewComponent = (
                            <FunnelView
                                board={board}
                                initialTrackedLabels={
                                    this.state.demoParams ? this.state.demoParams.initialSelectedStatuses : undefined
                                }
                            />
                        );
                        break;
                }
            } else {
                viewComponent = (
                    <div className="App__loading">
                        <Loading text="Loading" />
                        <div className="App__loading--progress">
                            <LinearProgressBar
                                size={LinearProgressBar.sizes.LARGE}
                                value={this.state.isLoadingPercentage}
                            />
                        </div>
                    </div>
                );
            }
        }

        return (
            <SettingsProvider settings={settings} context={context} labels={labels}>
                <div className="App" data-testid="app">
                    {!isRetrospectiveTools(settings.view || "") && <TopMenuFunnels />}
                    {viewComponent}
                    {needsRefresh > 0 && (
                        <Toast
                            type="info"
                            text={`${needsRefresh} change${needsRefresh > 1 ? "s" : ""}`}
                            buttonText="Refresh"
                            onButtonClick={this.handleRefresh}
                        />
                    )}
                    {demo && demoParams && <DemoOverlay name={demo} params={demoParams} />}
                </div>
            </SettingsProvider>
        );
    }
}
